import React, { useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Play } from '../../design/icons';
import './agent-system-film.css';

export interface Film {
    directory: string;
    name: string;
    title: React.ReactNode;
    description: React.ReactNode;
    seconds: number;
    articleSlug?: string;
    posterAlt: string;
    disclosure: string;
    transcript: { time: string; text: React.ReactNode }[];
    captionsTestId?: string;
}

export default function TechnicalFilm({ film, inline = false }: { film: Film; inline?: boolean }) {
    const media = `/media/${film.directory}`;
    const id = useId();
    const videoRef = useRef<HTMLVideoElement>(null);
    const [started, setStarted] = useState(false);
    const [unavailable, setUnavailable] = useState(false);
    const [playbackNotice, setPlaybackNotice] = useState('');

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;
        let mounted = true;
        const pause = () => { if (!video.paused) video.pause(); };
        const onVisibility = () => { if (document.visibilityState === 'hidden') pause(); };
        const observer = typeof IntersectionObserver === 'undefined' ? undefined :
            new IntersectionObserver(entries => {
                if (entries.some(entry => !entry.isIntersecting)) pause();
            });
        observer?.observe(video);
        document.addEventListener('visibilitychange', onVisibility);
        video.focus({ preventScroll: true });
        video.play().catch((error: unknown) => {
            if (!mounted || (error instanceof DOMException && error.name === 'AbortError')) return;
            console.error(`Could not start ${film.name}.`, error);
            if (error instanceof DOMException && error.name === 'NotAllowedError') {
                setPlaybackNotice('Press Play in the video player to begin.');
            } else {
                setUnavailable(true);
            }
        });
        return () => {
            mounted = false;
            observer?.disconnect();
            document.removeEventListener('visibilitychange', onVisibility);
            pause();
        };
    }, [started, unavailable, film.name]);

    return (
        <section className={`agent-system-film${inline ? ' agent-system-film--inline' : ''}`} aria-labelledby={`${id}-title`}>
            <div className="agent-system-film__copy">
                <h2 id={`${id}-title`}>{film.title}</h2>
                <p>{film.description}</p>
                {!inline && film.articleSlug && (
                    <Link className="agent-system-film__link" to={`/article/${film.articleSlug}`}>
                        Read the working note<ArrowRight size={18} />
                    </Link>
                )}
            </div>
            <div className="agent-system-film__media">
                <figure>
                    {unavailable ? (
                        <img className="agent-system-film__poster" src={`${media}/poster.webp`} width="1280" height="720"
                            alt={film.posterAlt} />
                    ) : started ? (
                        <video ref={videoRef} controls playsInline preload="none"
                            poster={`${media}/poster.webp`} src={`${media}/${film.directory}.mp4`}
                            width="1280" height="720" aria-label={`${film.name}: ${film.seconds}-second silent film`}
                            aria-describedby={`${id}-caption`} onError={() => setUnavailable(true)} onPlay={() => setPlaybackNotice('')}>
                            <track data-testid={film.captionsTestId ?? `${film.directory}-captions`} kind="captions" src={`${media}/captions.vtt`} srcLang="en" label="English text" />
                        </video>
                    ) : (
                        <button className="agent-system-film__start" type="button"
                            aria-label={`Play ${film.name}: ${film.seconds} seconds, silent`}
                            aria-describedby={`${id}-caption`} onClick={() => setStarted(true)}>
                            <img className="agent-system-film__poster" src={`${media}/poster.webp`} width="1280" height="720"
                                alt="" loading="lazy" decoding="async" />
                            <span className="agent-system-film__start-action"><Play size={18} />Play film</span>
                        </button>
                    )}
                    <figcaption id={`${id}-caption`}>{film.seconds}-second silent film. {film.disclosure}</figcaption>
                </figure>
                {unavailable && <p className="agent-system-film__error" role="status">Film playback is unavailable. Read the transcript below or try the MP4 file.</p>}
                {!unavailable && playbackNotice && <p className="agent-system-film__error" role="status">{playbackNotice}</p>}
                <div className="agent-system-film__reading">
                    <details>
                        <summary>Read the film transcript</summary>
                        <ol>
                            {film.transcript.map(cue => <li key={cue.time}><strong>{cue.time}.</strong> {cue.text}</li>)}
                        </ol>
                    </details>
                    <a href={`${media}/${film.directory}.mp4`} target="_blank" rel="noopener noreferrer">View full-size film</a>
                    <a href={`${media}/${film.directory}.mp4`} download>Download MP4</a>
                </div>
            </div>
        </section>
    );
}
